import React, { useEffect, useRef, useState, useCallback } from 'react'
import axios from 'axios';
import { TbSortAscending2Filled } from "react-icons/tb";

import { formatMongoDBTime } from '../../utills/dateAndTime';
import Spinner from '../../components/spinner';
import { useLanguage } from '../../context/languageContext';
import FilterPanel from '../../components/filterPanel';
import FilterBar from '../../components/filterBar';
import { useFilterChips } from '../../components/filterBar/useFilterChips';
import Pagination from '../../components/pagination';
import NoContent from '../../components/notFound/noBooks';
import { highlightSearchText } from '../../helper';
import OverflowTooltipCell from '../../components/tooltip';
import UserActivityModal from './UserActivityModal';
import styles from './users.module.scss';
import { joinList } from "../../helper/listSeparator";


function Users() {
  const { adminInfo } = useLanguage();
  const apiParamsRef = useRef(null);

  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalNumOfUsers, setTotalNumOfUsers] = useState(0);

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedField, setSelectedField] = useState('userName');

  const [sortOrder, setSortOrder] = useState('asc');

  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reportsData, setReportsData] = useState([]);
  const [notInterestedData, setNotInterestedData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const totalPages = Math.ceil(totalNumOfUsers / itemsPerPage);

  const fetchUserActivity = useCallback(async (userId) => {
    setActivityLoading(true);
    try {
      const headers = { Authorization: `Bearer ${adminInfo?.token}` };
      const { data: response } = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/api/admin/user-activity`,
        { params: { userId }, headers }
      );
      if (response?.status) {
        const data = response.data || response;
        setReportsData(data.reportsData || []);
        setNotInterestedData(data.notInterestedData || []);
      }
    } catch (error) {
      console.error("Error fetching user activity:", error);
      setReportsData([]);
      setNotInterestedData([]);
    } finally {
      setActivityLoading(false);
    }
  }, [adminInfo?.token]);

  const handleViewActivity = useCallback((user) => {
    setSelectedUser(user);
    setActivityModalOpen(true);
    const reports = user.reportsData || user.reports || [];
    const notInterested = user.notInterestedData || user.notInterested || [];
    if (reports.length > 0 || notInterested.length > 0) {
      setReportsData(reports);
      setNotInterestedData(notInterested);
      setActivityLoading(false);
    } else {
      fetchUserActivity(user._id);
    }
  }, [fetchUserActivity]);

  const handleCloseActivityModal = useCallback(() => {
    setActivityModalOpen(false);
    setSelectedUser(null);
    setReportsData([]);
    setNotInterestedData([]);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // The chip row under the search box. `chipFilters` is already in the

  // shape the endpoint reads, so it merges straight into `apiFilters`.

  const filters = useFilterChips();

  const apiFilters = {
    currentPage,
    itemsPerPage,
    langCode: "en",
    userName: selectedField === 'userName' ? debouncedSearchValue : '',
    email: selectedField === 'email' ? debouncedSearchValue : '',
    authorName: selectedField === 'authorName' ? debouncedSearchValue : '',
    createdAt: selectedField === "createdAt" ? debouncedSearchValue : '',
    sortOrder: sortOrder,
    // LAST on purpose: the single-field search above writes '' into every
    // field it is not searching, so a spread placed above it is wiped.
    ...filters.chipFilters,
    matchMode: filters.matchMode,
  };

  const filterOptions = [
    { value: 'userName', label: 'User Name' },
    { value: 'email', label: 'Email' },
    { value: 'authorName', label: 'Author Name' },
    { value: 'createdAt', label: "Date" }
  ];

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue, itemsPerPage, sortOrder, filters.signature]);

  useEffect(() => {
    if (!adminInfo?.token) {
      return;
    }

    document.title = "Penspace - Users";
    const keyValues = JSON.stringify({ currentPage, itemsPerPage, debouncedSearchValue, sortOrder, filters: filters.signature });

    if (apiParamsRef.current === keyValues) {
      return;
    }

    apiParamsRef.current = keyValues;
    fetchUsersData();
  }, [adminInfo, currentPage, itemsPerPage, debouncedSearchValue, sortOrder, filters.signature]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFieldChange = (field) => {
    setSelectedField(field);
    setSearchValue('');
    setDebouncedSearchValue('');
  };

  const handleSort = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };

  const fetchUsersData = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/allusers`, { filters: apiFilters }, { headers });

      if (response.status) {
        const userData = response?.data || {};

        setUsersData(userData.allUsersDocs);
        setTotalNumOfUsers(userData.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  }

  return (
    <React.Fragment>
      <div className="heading">
        <h4>{totalNumOfUsers > 1 ? `Users (${totalNumOfUsers})` : `User (${totalNumOfUsers})`}</h4>
      </div>
      <div className='d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 my-1'>
        <div className='flex-grow-1 w-100 w-lg-auto'>
          <FilterPanel
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            selectedField={selectedField}
            onFieldChange={handleFieldChange}
            filterOptions={filterOptions}
          />
        </div>
        {totalPages > 1 && (
          <div className='d-flex justify-content-center justify-content-lg-end w-100 w-lg-auto'>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalNumOfUsers}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showTotalItems={true}
            />
          </div>
        )}
      </div>
      <FilterBar
      options={filterOptions}
      chips={filters.chips}
      matchMode={filters.matchMode}
      onMatchModeChange={filters.setMatchMode}
      onAdd={filters.addChip}
      onUpdate={filters.updateChip}
      onRemove={filters.removeChip}
      onClear={filters.clearChips}
      />
      {loading ? (
        <Spinner />
      ) : (
        <div className={`table_scrollbar table-responsive ${styles.usersTableWrapper}`}>
          <table className={`table ${styles.usersTable}`}>
            <thead>
              <tr>
                <th>#</th>
                <th>User Name
                  <TbSortAscending2Filled
                    size={18}
                    style={{ cursor: 'pointer', marginLeft: '3px' }}
                    onClick={handleSort}
                    title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                  />
                </th>
                <th>Email</th>
                <th>Favorite Authors</th>
                <th>Preferred Genres</th>
                <th>Joined At</th>
                <th>Activity</th>
              </tr>
            </thead>
            <tbody>
              {usersData?.length > 0 ? (
                usersData.map((user, index) => {
                  const globalIndex = (currentPage - 1) * itemsPerPage + index;

                  return (
                    <tr key={user._id || index}>
                      <td className="index text-center">{globalIndex + 1}</td>
                      <td className={styles.cellContent}>
                        {selectedField === 'userName' ? highlightSearchText(user.userName, debouncedSearchValue) : user.userName}
                      </td>
                      <td className={styles.cellContent}>
                        {selectedField === 'email' ? highlightSearchText(user.email, debouncedSearchValue) : user.email}
                      </td>
                      <OverflowTooltipCell
                        className={`favoriteAuthors ${styles.favoriteAuthorsCell}`}
                        index={globalIndex}
                        idPrefix="favoriteAuthors"
                        content={joinList(user.favoriteAuthors?.map(item => item.authorName)) || '-'}
                      />
                      <OverflowTooltipCell
                        className={`${styles.genresCell}`}
                        index={globalIndex}
                        idPrefix="preferredGenres"
                        content={user.preferredGenres?.length > 0
                          ? joinList(user.preferredGenres.map((g) => g.categoryName))
                          : '-'}
                      />
                      <td className={styles.cellContent}>
                        {selectedField === 'createdAt' ? highlightSearchText(formatMongoDBTime(user.createdAt), debouncedSearchValue) : formatMongoDBTime(user.createdAt)}
                      </td>
                      <td className={styles.activityCell}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleViewActivity(user)}
                        >
                          View activity
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <NoContent searchValue={searchValue} title="Users" colSpan={7} />
              )}
            </tbody>
          </table>
        </div>
      )}

      <UserActivityModal
        isOpen={activityModalOpen}
        onClose={handleCloseActivityModal}
        userName={selectedUser?.userName}
        reportsData={reportsData}
        notInterestedData={notInterestedData}
        loading={activityLoading}
      />
    </React.Fragment>
  );
}

export default Users
